import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePendingSubmissions } from '@/common/hooks/lecturer/usePendingSubmissions';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useCalendarBadges } from '@/lecturer/EventCalendar/hooks/useCalendarBadges';
import { useAuthStore } from '@/common/auth/store';

/**
 * Hook to manage and provide data for the lecturer sidebar navigation.
 */
export const useLecturerSidebarData = () => {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Get modules and pending evaluations
  const { modules, isModulesLoading } = useLecturerModuleData(undefined, undefined, undefined);
  const { data: allPendingEvaluations } = usePendingSubmissions();

  // Filter pending evaluations to only include those from our own modules
  const pendingEvaluations = useMemo(() => {
    if (!modules || !allPendingEvaluations) return [];
    return allPendingEvaluations.filter((s) => modules.some((m) => String(m.id) === String(s.moduleId)));
  }, [modules, allPendingEvaluations]);

  const pendingCount = pendingEvaluations.length;
  const hasOverdueEvaluations = pendingEvaluations.some((s) => s.dueDate && new Date(s.dueDate) < new Date());

  // Get planning gaps for badge and loading state (tasks + mandates only, no slots)
  const {
    hasEventTasks, gapCount, hasCriticalGaps, tasksLoading,
  } = useCalendarBadges();

  // Partition modules by authorship: modules the lecturer owns vs. modules they
  // only hold a mandate on (e.g. evaluator). Authorship is the permission axis —
  // mandate-only modules open the editor read-only. Each module lands in exactly
  // one list; alphabetical within each.
  const { authoredModules, evaluatorModules } = useMemo(() => {
    const sorted = [...(modules ?? [])].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    const authored = sorted.filter((m) => !!currentUserId && m.author?.id === currentUserId);
    const evaluator = sorted.filter((m) => !(currentUserId && m.author?.id === currentUserId));
    return { authoredModules: authored, evaluatorModules: evaluator };
  }, [modules, currentUserId]);

  return {
    t,
    isModulesLoading,
    pendingCount,
    hasOverdueEvaluations,
    gapCount,
    hasCriticalGaps,
    hasEventTasks,
    authoredModules,
    evaluatorModules,
    isCalendarLoading: tasksLoading,
  };
};
